const HEIC_MIME_TYPES = new Set(["image/heic", "image/heif"]);
const PDF_MIME_TYPE = "application/pdf";
const GIF_MIME_TYPE = "image/gif";
const AVIF_MIME_TYPE = "image/avif";
const IMAGE_EXTENSIONS = new Set([
  ".png",
  ".jpg",
  ".jpeg",
  ".webp",
  ".heic",
  ".heif",
  ".avif",
]);

function getExtension(fileName = "") {
  const value = String(fileName || "");
  const index = value.lastIndexOf(".");
  if (index < 0) return "";
  return value.slice(index).toLowerCase();
}

function isGifFile(file) {
  if (!file) return false;
  const mime = String(file.type || "").toLowerCase();
  if (mime === GIF_MIME_TYPE) return true;
  return getExtension(file.name) === ".gif";
}

function isHeicFile(file) {
  if (!file) return false;
  const mime = String(file.type || "").toLowerCase();
  if (HEIC_MIME_TYPES.has(mime)) return true;
  const ext = getExtension(file.name);
  return ext === ".heic" || ext === ".heif";
}

function isPdfFile(file) {
  if (!file) return false;
  const mime = String(file.type || "").toLowerCase();
  if (mime === PDF_MIME_TYPE) return true;
  return getExtension(file.name) === ".pdf";
}

function isAvifFile(file) {
  if (!file) return false;
  const mime = String(file.type || "").toLowerCase();
  if (mime === AVIF_MIME_TYPE) return true;
  return getExtension(file.name) === ".avif";
}

function isImageLikeFile(file) {
  if (!file) return false;
  const mime = String(file.type || "").toLowerCase();
  if (mime.startsWith("image/")) return true;
  return IMAGE_EXTENSIONS.has(getExtension(file.name));
}

function normalizeOutputName(originalName = "photo.jpg", ext = "jpg") {
  const safeExt = String(ext || "jpg").replace(/^\./, "").toLowerCase();
  const baseName = String(originalName || "photo").replace(/\.[^/.]+$/, "");
  return `${baseName || "photo"}_upload.${safeExt}`;
}

function toBlob(canvas, type, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error(`Failed to convert canvas to ${type}.`));
          return;
        }
        resolve(blob);
      },
      type,
      quality,
    );
  });
}

async function loadImageFromBlob(blob) {
  const objectUrl = URL.createObjectURL(blob);
  try {
    const image = await new Promise((resolve, reject) => {
      const img = new window.Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("Image loading failed."));
      img.decoding = "async";
      img.src = objectUrl;
    });
    return image;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function computeScaledDimensions(width, height, maxLongEdge) {
  const safeWidth = Math.max(1, Number(width || 1));
  const safeHeight = Math.max(1, Number(height || 1));
  const longest = Math.max(safeWidth, safeHeight);

  if (!Number.isFinite(maxLongEdge) || maxLongEdge <= 0 || longest <= maxLongEdge) {
    return { width: safeWidth, height: safeHeight, scaled: false };
  }

  const ratio = maxLongEdge / longest;
  return {
    width: Math.max(1, Math.round(safeWidth * ratio)),
    height: Math.max(1, Math.round(safeHeight * ratio)),
    scaled: true,
  };
}

function hasTransparency(context, width, height) {
  const data = context.getImageData(0, 0, width, height).data;
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] < 255) return true;
  }
  return false;
}

function drawToCanvas(image, width, height) {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d", { willReadFrequently: true });
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.drawImage(image, 0, 0, width, height);
  return { canvas, context };
}

export async function prepareUploadFile(
  file,
  {
    maxLongEdge = 1920,
    targetMaxBytes = 1100 * 1024,
    qualityStart = 0.86,
    qualityMin = 0.64,
    qualityStep = 0.06,
  } = {},
) {
  if (!file) {
    throw new Error("No file provided.");
  }

  if (isGifFile(file)) {
    throw new Error("GIF files are not allowed.");
  }

  if (isPdfFile(file)) {
    return { file, optimized: false };
  }

  if (!isImageLikeFile(file)) {
    return { file, optimized: false };
  }

  const needsDecodeFallback = isHeicFile(file) || isAvifFile(file);
  let image;
  try {
    image = await loadImageFromBlob(file);
  } catch (error) {
    // Some browsers cannot decode HEIC/HEIF/AVIF reliably on the client.
    // In that case we keep the original and let backend conversion handle it.
    if (needsDecodeFallback) {
      return { file, optimized: false };
    }
    throw error;
  }

  const sourceWidth = image.naturalWidth || image.width || 1;
  const sourceHeight = image.naturalHeight || image.height || 1;
  const {
    width: drawWidth,
    height: drawHeight,
    scaled,
  } = computeScaledDimensions(sourceWidth, sourceHeight, maxLongEdge);

  if (!scaled && file.size <= targetMaxBytes) {
    return { file, optimized: false };
  }

  const { canvas, context } = drawToCanvas(image, drawWidth, drawHeight);
  const alpha = hasTransparency(context, drawWidth, drawHeight);

  let outputBlob;
  let outputExt;

  if (alpha) {
    try {
      outputBlob = await toBlob(canvas, "image/webp", 0.9);
      outputExt = "webp";
    } catch {
      outputBlob = await toBlob(canvas, "image/png");
      outputExt = "png";
    }
  } else {
    let quality = qualityStart;
    outputBlob = await toBlob(canvas, "image/jpeg", quality);

    while (outputBlob.size > targetMaxBytes && quality - qualityStep >= qualityMin) {
      quality -= qualityStep;
      outputBlob = await toBlob(canvas, "image/jpeg", quality);
    }

    if (outputBlob.size > targetMaxBytes) {
      const extraScale = Math.max(
        0.55,
        Math.min(0.95, Math.sqrt(targetMaxBytes / outputBlob.size)),
      );
      if (extraScale < 0.95) {
        const smallerWidth = Math.max(1, Math.round(drawWidth * extraScale));
        const smallerHeight = Math.max(1, Math.round(drawHeight * extraScale));
        const { canvas: smallerCanvas, context: smallerContext } = drawToCanvas(
          image,
          smallerWidth,
          smallerHeight,
        );
        outputBlob = await toBlob(
          smallerCanvas,
          "image/jpeg",
          Math.max(qualityMin, quality - qualityStep),
        );
        void smallerContext;
      }
    }

    outputExt = "jpg";
  }

  if (!scaled && outputBlob.size >= file.size) {
    return { file, optimized: false };
  }

  const outputType = outputBlob.type || "image/jpeg";
  const outputFile = new File(
    [outputBlob],
    normalizeOutputName(file.name || "photo.jpg", outputExt),
    {
      type: outputType,
      lastModified: Date.now(),
    },
  );

  return { file: outputFile, optimized: true };
}
